import { atom, type AtomMut } from '@reatom/framework'
import BigNumber from 'bignumber.js'

import {
  BigNumber as BigNumber_,
  Data,
  Map,
  Order,
  WeakRegistry,
} from '@blade/std-lib'

import { selectedNetworkAtom } from '../network'
import { DelugeId, UserShort } from '../user-short'
import { reatomWallet, WalletId } from '../wallet'

import { backOfficeWalletsRegistry } from './back-office-wallet'

import type { UserStatus } from '../auth'
import type { WalletModel } from '../wallet'
import type { BackOfficeUserDto } from '@root/shared/api/TradeApi'
import type { AvatarsHost, TokenLogosHost } from '@root/shared/models'

export class BackOfficeUserRegistry {
  private _registry = new WeakRegistry<DelugeId, BackOfficeUser>()

  resolveFromDto(args: {
    dto: BackOfficeUserDto
    avatarsHost: AvatarsHost
    imagesHost: TokenLogosHost
  }) {
    return this._registry.getOrCreate(DelugeId(args.dto.delugeId), () =>
      BackOfficeUser.fromDto(args),
    )
  }
}
export const backOfficeUserRegistry = new BackOfficeUserRegistry()
export interface BackOfficeUserData extends UserShort.UserShort {
  blocked: AtomMut<boolean>
  status: UserStatus
  tradeInfo: {
    solVolume: BigNumber
    solProfit: BigNumber
    ethVolume: BigNumber
    ethProfit: BigNumber
    tonVolume: BigNumber
    tonProfit: BigNumber
    usdVolume: BigNumber
    usdProfit: BigNumber
  }
  wallets: ReadonlyArray<WalletModel>
  createdAt: Date | undefined
  updatedAt: Date | undefined
}

export class BackOfficeUser extends Data.TaggedClass(
  'BackOfficeUser',
)<BackOfficeUserData> {
  walletsOfSelectedNetworkAtom = atom((ctx) => {
    const selectedNetwork = ctx.spy(selectedNetworkAtom)

    return this.wallets
      .filter((wallet) => wallet.network === selectedNetwork)
      .sort(
        Order.flip(
          Order.mapInput(BigNumber_.order, (x) =>
            ctx.spy(x.totalBalanceNative),
          ),
        ),
      )
  })

  static fromDto = (args: {
    dto: BackOfficeUserDto
    avatarsHost: AvatarsHost
    imagesHost: TokenLogosHost
  }): BackOfficeUser => {
    const { dto } = args

    return new BackOfficeUser({
      ...UserShort.fromDto(dto, args.avatarsHost),
      blocked: atom(dto.blocked, 'blocked'),
      status: dto.status,
      createdAt: dto.createdAt ? new Date(dto.createdAt) : undefined,
      updatedAt: dto.updatedAt ? new Date(dto.updatedAt) : undefined,
      wallets: dto.wallets.map((walletDto) => {
        return backOfficeWalletsRegistry.getOrCreate(
          WalletId(walletDto.walletId),
          () =>
            // eslint-disable-next-line @reatom/unit-naming-rule
            reatomWallet(walletDto, args.imagesHost, Map.create([])),
        )
      }),
      tradeInfo: {
        solVolume: new BigNumber(dto.tradeInfo.solVolume),
        solProfit: new BigNumber(dto.tradeInfo.solProfit),
        ethVolume: new BigNumber(dto.tradeInfo.ethVolume),
        ethProfit: new BigNumber(dto.tradeInfo.ethProfit),
        tonVolume: new BigNumber(dto.tradeInfo.tonVolume),
        tonProfit: new BigNumber(dto.tradeInfo.tonProfit),
        usdVolume: new BigNumber(dto.tradeInfo.usdVolume),
        usdProfit: new BigNumber(dto.tradeInfo.usdProfit),
      },
    })
  }
}
